import { useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { WhatsAppHeader } from '@/components/whatsapp/WhatsAppWorkspace';
import WhatsappAccountsList from './AccountsList';
import WhatsappAccountSettings from './AccountSettings';
import { Phone, Smartphone, X } from 'lucide-react';
import clsx from 'clsx';

// "Accounts" listed every connected number; "Profile" configured one of them.
// Same integration at two zoom levels, so they're tabs on one page — and the
// gear on a card switches tab rather than navigating away. Tab + selected
// account both live in the URL, so links and the back button work.
type Tab = 'accounts' | 'profile';

const TABS: Array<{ id: Tab; label: string; icon: React.ReactNode }> = [
  { id: 'accounts', label: 'Accounts', icon: <Phone size={14} /> },
  { id: 'profile', label: 'Profile', icon: <Smartphone size={14} /> },
];

export default function WhatsappAccountsManager() {
  const [params, setParams] = useSearchParams();
  const raw = params.get('tab');
  const tab: Tab = raw === 'profile' ? 'profile' : 'accounts';
  // AccountSettings reads `account` itself; undefined means the default account.
  const account = params.get('account') ?? undefined;

  const go = useCallback(
    (next: { tab: Tab; account?: string }) => {
      const p = new URLSearchParams();
      p.set('tab', next.tab);
      if (next.account) p.set('account', next.account);
      setParams(p);
    },
    [setParams],
  );

  return (
    <>
      <WhatsAppHeader
        title="Accounts"
        section="accounts"
        description="Connect WhatsApp Business numbers and manage the profile of each."
      />

      <div className="border-b border-border px-7">
        <div className="flex items-center gap-1">
          {TABS.map((t) => (
            <button
              key={t.id}
              // Carry the selected number into Profile; drop it going back to the list.
              onClick={() => go({ tab: t.id, account: t.id === 'profile' ? account : undefined })}
              className={clsx(
                'inline-flex items-center gap-2 border-b-2 px-3 py-2.5 text-sm font-medium transition-colors',
                tab === t.id
                  ? 'border-primary text-primary'
                  : 'border-transparent text-text-muted hover:text-text-primary',
              )}
            >
              {t.icon}
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {tab === 'profile' && account && (
        <div className="flex items-center gap-2 px-7 pt-4">
          <span className="text-xs text-text-muted">Editing</span>
          <span className="inline-flex items-center gap-1.5 rounded-full bg-primary-soft px-2.5 py-1 text-xs font-medium text-primary">
            {account}
            <button
              onClick={() => go({ tab: 'profile' })}
              className="rounded-full p-0.5 hover:bg-primary/20"
              aria-label="Edit the default account instead"
            >
              <X size={11} />
            </button>
          </span>
        </div>
      )}

      {tab === 'accounts' && (
        <WhatsappAccountsList
          embedded
          onOpenProfile={(accountUid) => go({ tab: 'profile', account: accountUid })}
        />
      )}
      {tab === 'profile' && <WhatsappAccountSettings embedded key={account ?? 'default'} />}
    </>
  );
}
